import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LoginFormStyle as S } from './LoginForm.style';
import { PButton } from 'components/prime/PButton/PButton';
import { LoginFormEmailField } from './components/LoginFormEmailField/LoginFormEmailField';
import { LoginFormPassword } from './components/LoginFormPassword/LoginFormPassword';
import { LoginFormRememberMe } from './components/LoginFormRememberMe/LoginFormRememberMe';
import { useLoginPageBLoC } from '../../LoginPage.bloc';
import { tokens } from 'services/tokens';
import { useObservableState } from 'observable-hooks';
import { useFormState } from 'react-hook-form';
import { LinkText } from 'components/LinkText/LinkText';
import { VStack } from 'components/layouts/primitives/Stack/Stack';

export const LoginForm: React.FC = () => {
  const formRef = useRef<HTMLFormElement | null>(null);
  const { lockout, form, $ssoLoading } = useLoginPageBLoC();
  const csrf = useObservableState(tokens.$csrfToken);
  const ssoLoading = useObservableState($ssoLoading);
  const { isValid } = useFormState(form);
  const [isLoading, setIsloading] = useState(false);

  const onSubmit = useCallback(() => {
    if (isValid && formRef?.current) {
      formRef.current?.submit();
      setIsloading(true);
    } else {
      form.trigger();
    }
  }, [isValid, formRef, form]);

  useEffect(() => {
    let form: HTMLFormElement | null = null;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') onSubmit();
    };
    if (formRef.current) {
      formRef.current.addEventListener('keydown', handleKeyDown);
      form = formRef.current;
    }
    return () => {
      if (form) form.removeEventListener('keydown', handleKeyDown);
    };
  }, [onSubmit, formRef]);

  return (
    <S.Form ref={formRef} role="form" method="POST" action="/login">
      <S.Title>Log in to your account</S.Title>
      <S.Subtitle>
        New to Yabble? <LinkText href="https://www.yabble.com/book-a-demo">Book a demo</LinkText>
      </S.Subtitle>
      {!!lockout && (
        <S.Message
          severity="error"
          content={`Too many failed attempts. Your account has been locked for ${lockout}. Please try again later or contact us.`}
          data-testid="label-login-too-many-attempts"
        />
      )}
      <VStack gap="gap5">
        <LoginFormEmailField />
        <LoginFormPassword />
        <LoginFormRememberMe />
      </VStack>
      <input type="hidden" name="_token" value={csrf || ''} />
      <S.Footer>
        <PButton
          className="w-full"
          label="Login"
          onClick={onSubmit}
          loading={isLoading}
          disabled={isLoading || ssoLoading}
          data-testid="button-login-submit"
        />
        <LinkText as="Link" to="/password/reset" data-testid="button-login-forgot-password">
          Forgot my password
        </LinkText>
        {/* <S.Divider>
          <div />
          <span>OR</span>
          <div />
        </S.Divider>
        <PButton
          className="w-full"
          severity="secondary"
          label="Log in using company credentials"
          onClick={ssoLogin}
          loading={ssoLoading}
          disabled={ssoLoading || isLoading}
        /> */}
      </S.Footer>
    </S.Form>
  );
};
